<template>
  <div>
    <!-- TABLE HISTORIQUE -->
    <div class="content">
      <div class="block">
        <div class="block-content block-content-full">
          <table class="table table-striped table-vcenter table-bordered">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Montant</th>
                <th class="text-center">Actions</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="invoice in invoices" :key="invoice.id">
                <td>{{ formatDate(invoice.created_at) }}</td>

                <td>
                  <span class="badge bg-primary rounded-pill">
                    {{ getLabel(invoice) }}
                  </span>
                </td>

                <td :class="{'text-danger': invoice.amount < 0}">
                  {{ formatPrice(invoice.amount) }}
                </td>

                <td class="text-center">
                  <button
                    v-if="canCorrectInvoice(invoice)"
                    type="button"
                    class="od-row-action"
                    @click="openDialog(invoice)"
                  >
                    Erreur de saisie
                  </button>
                </td>
              </tr>

              <tr v-if="!invoices.length">
                <td colspan="4" class="text-center text-muted">
                  Aucun paiement
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- DIALOG -->
    <v-dialog
      v-model="dialog"
      max-width="560"
      persistent
      content-class="od-dialog"
      overlay-color="#171b23"
      overlay-opacity="0.55"
      transition="invoice-cancel-slide-y-transition"
    >
      <div class="od-dialog-card">
        <div class="catalogue-modal-header">
          <div>
            <p class="catalogue-modal-eyebrow">Paiement</p>
            <h2 class="catalogue-modal-title">Corriger une erreur de saisie</h2>
            <p class="catalogue-modal-subtitle">Le paiement saisi est annulé puis recréé avec le moyen et le montant corrigés.</p>
          </div>
          <button type="button" class="catalogue-modal-close" aria-label="Fermer" :disabled="loading" @click="closeDialog">
            <img src="/media/svg/handoff-icons/svg/x.svg" alt="" aria-hidden="true">
          </button>
        </div>

        <div v-if="selected" class="catalogue-modal-body">
          <div class="od-dialog-summary">
            <div>
              <div class="od-dialog-summary-label">Paiement d'origine</div>
              <div class="od-dialog-summary-value">
                {{ selected.payment_method?.name || 'Non spécifié' }} · {{ formatPrice(selected.amount) }}
              </div>
              <div class="od-dialog-summary-meta">{{ formatDate(selected.created_at) }}</div>
            </div>
            <span class="od-dialog-pill is-warning">Correction</span>
          </div>

          <div class="od-dialog-field">
            <label class="catalogue-modal-label" :for="`od-correction-method-${_uid}`">Nouveau moyen de paiement</label>
            <select :id="`od-correction-method-${_uid}`" v-model="selectedMethod" class="catalogue-modal-select" :disabled="methodsLoading">
              <option :value="null" disabled>{{ methodsLoading ? 'Chargement…' : 'Choisir un moyen de paiement' }}</option>
              <option v-for="method in methods" :key="method.id" :value="method.id">{{ method.name }}</option>
            </select>
          </div>

          <div class="od-dialog-field">
            <label class="catalogue-modal-label" :for="`od-correction-amount-${_uid}`">Montant corrigé</label>
            <div class="od-dialog-suffix">
              <input
                :id="`od-correction-amount-${_uid}`"
                v-model="amount"
                type="number"
                step="0.01"
                min="0.01"
                :max="Math.abs(selected.amount)"
                class="catalogue-modal-input"
                :class="{ 'is-invalid': amountError }"
              >
              <span>€</span>
            </div>
            <div v-if="amountError" class="od-dialog-help is-error">{{ amountError }}</div>
          </div>

          <div class="od-dialog-field">
            <label class="catalogue-modal-label" :for="`od-correction-note-${_uid}`">Explication</label>
            <textarea
              :id="`od-correction-note-${_uid}`"
              v-model="note"
              class="catalogue-modal-input"
              rows="3"
              maxlength="500"
              placeholder="Exemple : erreur de saisie du montant encaissé"
            ></textarea>
            <div class="od-dialog-help"><span>Facultatif</span><span>{{ (note || '').length }}/500</span></div>
          </div>

          <div v-if="apiError" class="od-dialog-error">{{ apiError }}</div>
        </div>

        <div class="catalogue-modal-footer">
          <button type="button" class="catalogue-modal-cancel" :disabled="loading" @click="closeDialog">Annuler</button>
          <button type="button" class="catalogue-modal-submit" :disabled="!canSubmit || loading" @click="submit">
            <span v-if="loading" class="od-dialog-spinner" aria-hidden="true"></span>
            Corriger le paiement
          </button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios"
import moment from "moment"
import "moment/locale/fr"

export default {
  props: {
    invoices: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      dialog: false,
      selected: null,

      methods: [],
      methodsLoading: false,
      selectedMethod: null,

      amount: null,
      note: "",

      loading: false,
      apiError: null,

      rules: {
        required: v => !!v || "Champ requis",
        float: v => {
          const n = parseFloat(v)
          return !isNaN(n) || "Montant invalide"
        },
        positive: v => parseFloat(v) > 0 || "Le montant doit être supérieur à 0",
        maxSelectedAmount: v => {
          const max = parseFloat(this.selected?.amount || 0)
          return parseFloat(v) <= max || `Maximum ${max.toFixed(2)} €`
        },
      }
    }
  },

  computed: {
    amountError() {
      if (!this.selected || this.amount === null || this.amount === '') return ''
      const value = parseFloat(this.amount)
      const max = Math.abs(parseFloat(this.selected.amount || 0))
      if (isNaN(value)) return 'Montant invalide'
      if (value <= 0) return 'Le montant doit être supérieur à 0'
      if (value > max) return `Maximum ${max.toFixed(2)} €`
      return ''
    },
    canSubmit() {
      return (
        this.selected &&
        this.selectedMethod &&
        this.amount !== null &&
        !isNaN(parseFloat(this.amount)) &&
        parseFloat(this.amount) > 0 &&
        parseFloat(this.amount) <= parseFloat(this.selected.amount)
      )
    }
  },

  methods: {
    canCorrectInvoice(invoice) {
      return parseFloat(invoice.amount || 0) > 0
        && !invoice.virtual_fidelity_points
        && invoice.payment_method?.name !== 'Points de fidélité'
        && !this.hasStripeTransaction(invoice)
    },

    hasStripeTransaction(invoice) {
      return String(invoice.payment_method?.name || '').trim().toLowerCase() === 'stripe'
        || !!invoice.payment_intent_id
    },

    async openDialog(invoice) {
      this.selected = invoice
      this.amount = invoice.amount
      this.note = ""
      this.apiError = null
      this.dialog = true

      await this.loadMethods()

      this.selectedMethod = invoice.payment_method_id
      console.log(invoice)
    },

    closeDialog() {
      this.dialog = false
      this.selected = null
      this.amount = null
      this.note = ""
      this.selectedMethod = null
      this.apiError = null
    },

    async loadMethods() {
      this.methodsLoading = true
      try {
        const res = await axios.get('/back-office/paiement-method/list')
        this.methods = (res.data || []).filter(method => !method.hidden)
      } catch (e) {
        this.apiError = "Impossible de charger les méthodes de paiement."
      } finally {
        this.methodsLoading = false
      }
    },

    async submit() {
      if (!this.canSubmit) return

      this.loading = true
      this.apiError = null
      console.log('im in');
      try {
        await axios.post('/back-office/caisse/errorOrderInvoice', {
          payment_id: this.selected.id, // id de la transaction
          method_id: this.selectedMethod, // new payment method
          original_payment_method_id: this.selected.payment_method_id, // id original
          amount: parseFloat(this.amount),
          note: this.note,
          update_order: false,
        })

        this.closeDialog()
        window.location.reload()
      } catch (e) {
        this.apiError =
          e?.response?.data?.message || "Une erreur est survenue."
      } finally {
        this.loading = false
      }
    },

    formatDate(date) {
      moment.locale('fr')
      return moment(date).format('DD/MM/YYYY HH:mm')
    },

    formatPrice(price) {
      const value = parseFloat(price || 0)
      return value.toFixed(2) + " €"
    },

    getLabel(inv) {
      if (inv.serie) return "Abonnement"
      if (inv.treatment) return "Cure"
      if (inv.custom_discount != 0) return "Remise personnalisée"
      return inv.payment_method?.name || "Paiement"
    }
  }
}
</script>

<style src="./order-dialog.css"></style>

<style scoped>
::v-deep .invoice-cancel-slide-y-transition-enter-active,
::v-deep .invoice-cancel-slide-y-transition-leave-active {
  transition: opacity .22s ease, transform .22s ease;
}

::v-deep .invoice-cancel-slide-y-transition-enter,
::v-deep .invoice-cancel-slide-y-transition-leave-to {
  opacity: 0;
  transform: translateY(-32px);
}

::v-deep .invoice-cancel-slide-y-transition-enter-to,
::v-deep .invoice-cancel-slide-y-transition-leave {
  opacity: 1;
  transform: translateY(0);
}
</style>
